/**
 * /blog — Agency Blog category hub.
 *
 * Shows the 4 canonical categories as large tiles with per-category
 * post counts and a hover/preview of the most recent post. Clicking a
 * tile drills into /blog/category/<slug>.
 *
 * The list-of-all-posts experience moved to each category page; this
 * page is a navigation tier, not a content list.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    ArrowRight,
    HeartPulse,
    Home,
    Briefcase,
    ShieldAlert,
    Loader2,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import FooterBar from "@/components/FooterBar";
import useSeo from "@/lib/useSeo";
import { CATEGORY_META } from "@/lib/blogCategories";

const API = "/api";

// Icon + accent colour per canonical category slug. Keep keys in sync
// with /app/backend/blog.py BLOG_CATEGORIES.
const CATEGORY_VISUALS = {
    health: {
        Icon: HeartPulse,
        accent: "from-emerald-500/15 via-emerald-500/5 to-transparent",
        accentBorder: "border-emerald-500/30",
        kicker: "Coverage updates",
    },
    personal: {
        Icon: Home,
        accent: "from-sky-500/15 via-sky-500/5 to-transparent",
        accentBorder: "border-sky-500/30",
        kicker: "Home & auto",
    },
    business: {
        Icon: Briefcase,
        accent: "from-amber-500/15 via-amber-500/5 to-transparent",
        accentBorder: "border-amber-500/30",
        kicker: "Commercial lines",
    },
    "special-risk": {
        Icon: ShieldAlert,
        accent: "from-rose-500/15 via-rose-500/5 to-transparent",
        accentBorder: "border-rose-500/30",
        kicker: "Specialty & E&S",
    },
};

export default function BlogListPage() {
    useSeo({
        title: "Agency Blog — Coverage Notes & Insurance Insights",
        description:
            "Recent updates, coverage notes, and market commentary from Endurance Casualty Insurance Agency. Browse Health, Personal, Business, and Special Risk topics.",
        canonicalPath: "/blog",
    });

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${API}/blog/categories`, {
                headers: { "Cache-Control": "no-cache" },
                params: { _: Date.now() },
            })
            .then((res) => {
                if (cancelled) return;
                setCategories(Array.isArray(res.data) ? res.data : []);
                setError(null);
            })
            .catch(() => {
                if (cancelled) return;
                setError(
                    "We couldn't reach the blog feed. This is usually a brief connection hiccup — please retry."
                );
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [retryCount]);

    return (
        <main
            data-testid="blog-list-page"
            className="flex min-h-screen flex-col bg-brand-bg text-brand-ivory"
        >
            <PageHeader />
            <section className="flex-1 px-5 py-12 sm:px-8 md:py-16">
                <div className="mx-auto max-w-5xl">
                    <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Agency Blog
                    </p>
                    <h1
                        data-testid="blog-list-title"
                        className="mt-4 font-serif text-4xl leading-tight text-brand-ivory sm:text-5xl"
                    >
                        Notes from the desk.
                    </h1>
                    <div className="gold-hairline my-6 w-32" />
                    <p className="max-w-2xl text-base leading-relaxed text-brand-ivory/85">
                        Pick a topic. We publish coverage commentary,
                        regulatory updates, and seasonal guidance from licensed
                        agents — sorted by category so you can dive into the
                        area that matters most.
                    </p>

                    {/* Substantive intro copy — lifts text-to-HTML ratio and
                        gives crawlers real keyword density on the hub page. */}
                    <div
                        data-testid="blog-list-intro"
                        className="mt-6 max-w-3xl space-y-4 text-sm leading-relaxed text-brand-ivory/80 sm:text-base"
                    >
                        <p>
                            The Endurance Casualty Agency Blog is a living
                            reference for households, business owners, and
                            benefits administrators trying to make sense of
                            insurance in plain English. Every article is
                            written or reviewed by a licensed producer working
                            on real client placements across New York
                            (NY), New Jersey (NJ), Pennsylvania (PA),
                            Connecticut (CT), North Carolina (NC), Florida
                            (FL), and Texas (TX) — not by anonymous content
                            marketers.
                        </p>
                        <p>
                            We split coverage into four lenses so you can
                            jump straight to the conversation you need:
                            {" "}
                            <Link
                                to="/blog/category/health"
                                className="text-brand-gold2 underline-offset-4 hover:underline"
                            >
                                Health
                            </Link>{" "}
                            for Medicare, Medigap, Medicaid, and ACA
                            individual plans;{" "}
                            <Link
                                to="/blog/category/personal"
                                className="text-brand-gold2 underline-offset-4 hover:underline"
                            >
                                Personal
                            </Link>{" "}
                            for home, auto, flood, and umbrella;{" "}
                            <Link
                                to="/blog/category/business"
                                className="text-brand-gold2 underline-offset-4 hover:underline"
                            >
                                Business
                            </Link>{" "}
                            for workers&apos; compensation, general
                            liability, BOP, and commercial auto; and{" "}
                            <Link
                                to="/blog/category/special-risk"
                                className="text-brand-gold2 underline-offset-4 hover:underline"
                            >
                                Special Risk
                            </Link>{" "}
                            for cyber liability, specialty E&amp;S, and the
                            placements most carriers won&apos;t quote in
                            three lines.
                        </p>
                        <p>
                            Nothing on this site binds insurance, guarantees
                            eligibility, or replaces a quote. When you find
                            an article that hits close to your situation,
                            use the subscribe block or the contact form to
                            talk it through with a real agent —{" "}
                            <Link
                                to="/contact"
                                className="text-brand-gold2 underline-offset-4 hover:underline"
                            >
                                we typically reply within one business day
                            </Link>
                            .
                        </p>
                    </div>

                    {loading ? (
                        <div className="mt-12 flex items-center gap-3 rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13] p-8 text-sm text-brand-muted">
                            <Loader2 className="h-4 w-4 animate-spin text-brand-gold" />
                            Loading topics…
                        </div>
                    ) : error ? (
                        <div
                            data-testid="blog-list-error"
                            className="mt-12 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 text-rose-200"
                        >
                            <p>{error}</p>
                            <button
                                type="button"
                                onClick={() => {
                                    setLoading(true);
                                    setError(null);
                                    setRetryCount((n) => n + 1);
                                }}
                                data-testid="blog-list-retry"
                                className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-300/50 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-rose-500/20"
                            >
                                Retry
                            </button>
                        </div>
                    ) : (
                        <ul
                            className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2"
                            data-testid="blog-category-grid"
                        >
                            {categories.map((cat) => {
                                const visuals =
                                    CATEGORY_VISUALS[cat.slug] ||
                                    CATEGORY_VISUALS.business;
                                const { Icon, accent, accentBorder, kicker } =
                                    visuals;
                                const isEmpty = (cat.post_count || 0) === 0;
                                return (
                                    <li key={cat.slug}>
                                        <Link
                                            to={`/blog/category/${cat.slug}`}
                                            data-testid={`blog-category-tile-${cat.slug}`}
                                            className={`group relative block h-full overflow-hidden rounded-2xl border bg-[#0b0e13] p-8 transition-colors hover:border-[rgba(212,175,55,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold ${accentBorder}`}
                                        >
                                            <div
                                                className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${accent}`}
                                            />
                                            <div className="relative">
                                                <div className="flex items-start justify-between">
                                                    <div
                                                        className={`inline-flex h-12 w-12 items-center justify-center rounded-xl border bg-[#05070a] ${accentBorder}`}
                                                    >
                                                        <Icon className="h-5 w-5 text-brand-gold" />
                                                    </div>
                                                    <span className="rounded-full border border-[rgba(212,175,55,0.25)] px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-brand-goldSoft">
                                                        {cat.post_count}{" "}
                                                        {cat.post_count === 1
                                                            ? "post"
                                                            : "posts"}
                                                    </span>
                                                </div>
                                                <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                                    {kicker}
                                                </p>
                                <h2 className="mt-2 font-serif text-3xl text-brand-ivory">
                                                    {CATEGORY_META[cat.slug]?.shortLabel || cat.label}
                                                </h2>
                                                <p className="mt-3 text-sm text-brand-muted sm:text-base">
                                                    {cat.tagline}
                                                </p>
                                                <p className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-brand-gold2">
                                                    {isEmpty
                                                        ? "More coming soon"
                                                        : "Browse articles"}
                                                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                                                </p>
                                            </div>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </section>
            <FooterBar />
        </main>
    );
}
